<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import SplitButton from 'dashboard/components-next/button/SplitButton.vue';
import SidebarToggle from 'dashboard/components-next/SidebarToggle/SidebarToggle.vue';
import CreateNewContactPanel from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactPanel.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
  sidebarCollapsed: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
  'expandSidebar',
]);

const { t } = useI18n();
const store = useStore();
const createNewContactPanelRef = ref(null);

const addContactMenuItems = computed(() => [
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.IMPORT_CONTACT'),
    action: 'import',
    value: 'import',
    icon: 'i-lucide-download',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.EXPORT_CONTACT'),
    action: 'export',
    value: 'export',
    icon: 'i-lucide-upload',
  },
]);

const handleAddAction = ({ action }) => {
  if (action === 'import') emit('import');
  else if (action === 'export') emit('export');
};

const openCreateContactDialog = () => {
  createNewContactPanelRef.value?.open();
};

const createContact = async contact => {
  try {
    await store.dispatch('contacts/create', contact);
    createNewContactPanelRef.value?.onSuccess();
  } catch (error) {
    useAlert(
      error.message ||
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <header class="sticky top-0 z-10 px-6">
    <div class="flex h-[49px] items-center w-full gap-2">
      <SidebarToggle
        v-if="sidebarCollapsed"
        collapsed
        @toggle="emit('expandSidebar')"
      />
      <div v-if="showSearch" class="flex min-w-0 flex-1 items-center">
        <Input
          :model-value="searchValue"
          type="search"
          :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
          ]"
          class="w-full"
          @input="emit('search', $event.target.value)"
        />
      </div>
      <div class="flex flex-shrink-0 items-center gap-2">
        <div v-if="!isLabelView && !isActiveView" class="relative">
          <Button
            id="toggleContactsFilterButton"
            :icon="
              isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
            "
            color="slate"
            size="sm"
            class="relative w-8"
            variant="ghost"
            @click="emit('filter')"
          >
            <div
              v-if="hasActiveFilters && !isSegmentsView"
              class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-brand"
            />
          </Button>
          <slot name="filter" />
        </div>
        <Button
          v-if="
            hasActiveFilters && !isSegmentsView && !isLabelView && !isActiveView
          "
          icon="i-lucide-save"
          color="slate"
          size="sm"
          variant="ghost"
          @click="emit('createSegment')"
        />
        <Button
          v-if="isSegmentsView && !isLabelView && !isActiveView"
          icon="i-lucide-trash"
          color="slate"
          size="sm"
          variant="ghost"
          @click="emit('deleteSegment')"
        />
        <ContactSortMenu
          :active-sort="activeSort"
          :active-ordering="activeOrdering"
          @update:sort="emit('update:sort', $event)"
        />
        <SplitButton
          icon="i-lucide-user-plus"
          :label="
            $t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ADD_CONTACT')
          "
          size="sm"
          :menu-items="addContactMenuItems"
          @click="openCreateContactDialog"
          @action="handleAddAction"
        />
        <CreateNewContactPanel
          ref="createNewContactPanelRef"
          @create="createContact"
        />
      </div>
    </div>
  </header>
</template>
